import ProductFactDialoguePanel from './ProductFactDialoguePanel';
import { isLegalDecisionKey, productFactsOnly, isProductQuestion, productDialogue } from './productFactDialogue';
import { researchDescriptionProposal } from './subdivisionPreviewClient';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  Bot,
  Check,
  CheckCircle2,
  ExternalLink,
  FileSearch,
  FileText,
  Loader2,
  Paperclip,
  RefreshCw,
  Search,
  Send,
  ShieldCheck,
} from "lucide-react";
import AiModePanel from "./AiModePanel";
import ProveDecisionGraph from "./ProveDecisionGraph";
import ProveEvaluationSummary from "./ProveEvaluationSummary";
import { isProveFinal } from "./proveFinality";
import {
  analyzeWorkspaceAttachment,
  canUseWorkspaceApi,
  ensureWorkspace,
  runWorkspaceAssistant,
  uploadWorkspaceAttachment,
  workspaceIdForQuery,
} from "./classificationWorkspaceApi";
import "./AiClassificationWorkspace.css";

import { factLabel, valueLabel } from "./proveFactLabels";
import { CONFIRMED_FACTS_SCHEMA, hasTreeFactSnapshot, loadTreeFacts, loadUnverifiedTreeFacts, saveTreeFacts, TREE_FACTS_UPDATED_EVENT, treeFactKey } from "./treeFactStore";

function safeHttpUrl(value) {
  try {
    const url = new URL(String(value || ""));
    return ["http:", "https:"].includes(url.protocol) ? url.href : "";
  } catch (_) {
    return "";
  }
}

function formatHsk(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 10) return `${digits.slice(0, 4)}.${digits.slice(4, 6)}-${digits.slice(6)}`;
  if (digits.length === 6) return `${digits.slice(0, 4)}.${digits.slice(4)}`;
  return digits || "—";
}

function savedAssistant(workspace) {
  return workspace?.state?.assistant || {};
}

function proveQueryText(query, productDetailsInput = {}) {
  // 딥링크·재검색처럼 상품 정보 없이 검색하면 null이 넘어온다(기본값은 undefined에만 적용).
  const productDetails = productDetailsInput || {};
  const clean = (value) => String(value || "").trim();
  return [
    clean(query),
    clean(productDetails.material) && `재질·구성: ${clean(productDetails.material)}`,
    clean(productDetails.intendedUse) && `용도·사용처: ${clean(productDetails.intendedUse)}`,
    clean(productDetails.construction) && `형태·작동방식: ${clean(productDetails.construction)}`,
    clean(productDetails.articleRole) && `품목 상태: ${clean(productDetails.articleRole)}`,
  ].filter(Boolean).join("\n");
}

function normalizeMessages(value) {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item) => ["user", "assistant"].includes(item?.role) && item?.content)
    .map((item) => ({
      role: item.role,
      content: String(item.content).replace(
        "제61·62류 심볼릭 결정트리의 적용 범위 밖",
        "PROVE 결정트리가 입력된 상품 사실만으로 단일 세번 경로를 선택하지 못한 상태",
      ),
    }))
    .slice(-40);
}

function classificationReport(q, response) {
  const tree = response?.classification_tree || response?.apparel_tree || {};
  const treeHsk = isProveFinal(response)
    ? tree?.supported_hsk : null;
  if (!treeHsk) return null;
  return {
    product_name: q,
    decided_hsk: treeHsk,
    decision_source: "prove_decision_tree",
    review_needed: false,
    ai_summary: response?.answer || "",
    evidence: response?.fact_ledger || [],
    decision_trace: tree.decision_trace || [],
    source_references: tree.source_references || [],
    factual_candidates: tree.factual_candidates || [],
    candidates: [],
  };
}

function inlineMarkdown(text, keyPrefix) {
  const source = String(text || "");
  const pattern = /(\*\*[^*\n]+\*\*|`[^`\n]+`|\[[^\]\n]+\]\(https?:\/\/[^\s)]+\))/g;
  const nodes = [];
  let cursor = 0;
  let match;
  while ((match = pattern.exec(source)) !== null) {
    if (match.index > cursor) nodes.push(source.slice(cursor, match.index));
    const token = match[0];
    if (token.startsWith("**")) {
      nodes.push(<strong key={`${keyPrefix}-strong-${match.index}`}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith("`")) {
      nodes.push(<code key={`${keyPrefix}-code-${match.index}`}>{token.slice(1, -1)}</code>);
    } else {
      const link = token.match(/^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/);
      const href = safeHttpUrl(link?.[2]);
      nodes.push(href
        ? <a key={`${keyPrefix}-link-${match.index}`} href={href} target="_blank" rel="noreferrer">{link[1]}</a>
        : token);
    }
    cursor = pattern.lastIndex;
  }
  if (cursor < source.length) nodes.push(source.slice(cursor));
  return nodes;
}

function MarkdownContent({ content, className = "" }) {
  const lines = String(content || "").replace(/\r\n?/g, "\n").split("\n");
  return (
    <div className={`aiw-markdown ${className}`.trim()}>
      {lines.map((line, index) => {
        const key = `${index}-${line.slice(0, 16)}`;
        const heading = line.match(/^#{1,4}\s+(.+)$/);
        if (heading) return <h3 key={key}>{inlineMarkdown(heading[1], key)}</h3>;
        const bullet = line.match(/^[-*]\s+(.+)$/);
        if (bullet) return <div className="aiw-md-list-item" key={key}><span>•</span><div>{inlineMarkdown(bullet[1], key)}</div></div>;
        const ordered = line.match(/^(\d+)[.)]\s+(.+)$/);
        if (ordered) return <div className="aiw-md-list-item" key={key}><span>{ordered[1]}.</span><div>{inlineMarkdown(ordered[2], key)}</div></div>;
        if (!line.trim()) return <div className="aiw-md-spacer" key={key} aria-hidden="true" />;
        return <p key={key}>{inlineMarkdown(line, key)}</p>;
      })}
    </div>
  );
}

export function visibleAssistantAnswer(content) {
  // Older persisted conversations may still contain the retired comparison rows.
  // Keep stored audit/history intact; only suppress these known presentation rows.
  return String(content || "").split(/\r?\n/)
    .filter(line => !/^\s*[-*]\s*(?:기존 HSK 분류기 후보|설명 AI·기존 분류기 비교 후보)\s*:/.test(line))
    .join("\n");
}

function AssistantAnswer({ content }) {
  return <MarkdownContent content={visibleAssistantAnswer(content)} className="aiw-answer" />;
}

function compactResearchSummary(value) {
  return String(value || "")
    .replace(/\[([^\]]+)\]\(https?:\/\/[^\s)]+\)/g, "$1")
    .replace(/https?:\/\/\S+/g, "")
    .replace(/[ \t]+\n/g, "\n")
    .trim();
}

function treeActionLabel(action) {
  if (action === "out_of_scope") return "자동판정 경로 확인 필요";
  if (action === "supported") return "심볼릭 후보 계산 완료";
  if (action === "supported_candidate") return "법정 후보 계산·상품 사실 확인 필요";
  if (action === "supported_hs6") return "HS6 판정·국내세분 확인 필요";
  if (action === "ask") return "추가 사실 확인 필요";
  if (action === "ambiguous") return "복수 경로·사실 확인 필요";
  return action || "확인 필요";
}

const PROVE_WORKFLOW_STEPS = [
  "PROVE 1차 판정",
  "AI 웹검증",
  "상품 사실 확인",
  "PROVE 재계산",
  "판정 완료",
];

function fallbackWorkflow(tree, comparison, result, proved) {
  const action = tree.action || "out_of_scope";
  const candidateReady = String(action).startsWith("supported");
  const effectiveStatus = comparison.effective_status
    || (result.requires_identity_confirmation ? "confirm_product_identity" : "needs_facts");
  const workflow = (code, label, description, actionLabel, phase, stage, confirmationTarget, final = false) => ({
    code,
    label,
    description,
    action_label: actionLabel,
    phase,
    stage,
    stage_count: 5,
    candidate_ready: candidateReady,
    final,
    confirmation_target: confirmationTarget,
    user_confirms_hsk: false,
    decision_owner: "prove_symbolic_engine",
  });

  if (proved) return workflow(
    "proved",
    "PROVED · 판정 완료",
    "확인된 상품 사실과 운영 규칙으로 PROVE가 세번을 판정했습니다.",
    "판정 결과 보기",
    "completed",
    5,
    "none",
    true,
  );
  if (effectiveStatus === "confirm_product_identity") return workflow(
    "document_identity_confirmation_required",
    candidateReady ? "후보 계산 완료 · 서류 동일성 확인 필요" : "서류 동일성 확인 필요",
    "첨부 서류가 현재 분류할 상품의 자료인지 확인하면 PROVE가 같은 규칙으로 다시 계산합니다.",
    "이 상품의 서류인지 확인",
    "user_confirmation",
    3,
    "document_identity",
  );
  if (effectiveStatus === "resolve_fact_conflict"
      || (comparison.unresolved_fact_conflicts || []).length > 0) return workflow(
    "fact_conflict_resolution_required",
    "상품 사실 충돌 확인 필요",
    "서류와 사용자 입력에서 서로 다르게 확인된 상품 사실을 먼저 해소해야 합니다.",
    "충돌 사실 해소",
    "user_confirmation",
    3,
    "fact_conflict",
  );
  if (effectiveStatus === "confirm_interpreted_facts"
      || comparison.requires_interpreted_fact_confirmation) return workflow(
    "product_fact_confirmation_required",
    candidateReady ? "후보 계산 완료 · 상품 사실 확인 필요" : "추출된 상품 사실 확인 필요",
    candidateReady
      ? "PROVE가 후보를 계산했습니다. 자연어에서 추출한 상품 사실이 맞는지만 확인하면 같은 결정트리로 재계산합니다."
      : "자연어에서 추출한 상품 사실이 맞는지 확인하면 PROVE가 결정트리를 다시 계산합니다.",
    "상품 사실 확인",
    "user_confirmation",
    3,
    "product_facts",
  );
  if (effectiveStatus === "confirm_live_rule_source") return workflow(
    "expert_rule_review_required",
    "전문가 검토 필요",
    "상품 사실은 충분하지만 운영 규칙 원천의 활성 상태를 전문가가 확인해야 합니다.",
    "운영 규칙 검토",
    "expert_review",
    4,
    "expert_review",
  );
  if (action === "ambiguous") return workflow(
    "ambiguous_path_review_required",
    "복수 경로 검토 필요",
    "둘 이상의 법정 경로가 남아 있어 경로를 가르는 상품 사실이 더 필요합니다.",
    "구분 사실 확인",
    "user_confirmation",
    3,
    "product_facts",
  );
  if (action === "supported_hs6") return workflow(
    "domestic_subdivision_information_required",
    "국내세분 정보 필요",
    "국제 6단위까지 판정했으며 국내 10단위를 가르는 상품 정보를 확인해야 합니다.",
    "국내세분 정보 입력",
    "information_collection",
    3,
    "product_facts",
  );
  if (action === "out_of_scope" && tree.reason === "supported_decision_tree_signal_not_found") return workflow(
    "additional_product_information_required",
    "추가 상품정보 필요",
    "현재 입력된 상품 사실만으로 운영 결정트리가 단일 세번 경로를 선택하지 못했습니다.",
    "상품정보 추가",
    "information_collection",
    2,
    "product_facts",
  );
  if (action === "out_of_scope") return workflow(
    "out_of_scope",
    "자동판정 경로 확인 필요",
    "현재 운영 결정트리가 이 상품을 단일 세번 경로로 판정하지 못했습니다.",
    "상품 사실·경로 확인",
    "scope_check",
    2,
    "expert_review",
  );
  return workflow(
    "additional_product_information_required",
    "추가 상품정보 필요",
    "결정트리가 다음 갈림축을 계산하려면 재질·구성·용도·형태 같은 상품 정보가 더 필요합니다.",
    "추가 상품정보 입력",
    "information_collection",
    2,
    "product_facts",
  );
}

function proveWorkflow(result, tree, comparison, prove, proved) {
  const fallback = fallbackWorkflow(tree, comparison, result, proved);
  const supplied = prove?.workflow;
  if (!supplied?.code || (supplied.final === true && !proved)) return fallback;
  return {
    ...fallback,
    ...supplied,
    stage: Number(supplied.stage || fallback.stage),
    stage_count: Number(supplied.stage_count || 5),
    final: proved,
  };
}

function workflowTone(workflow) {
  if (workflow.final) return "success";
  if (["fact_conflict_resolution_required", "ambiguous_path_review_required"].includes(workflow.code)) return "danger";
  return "pending";
}

function ProveWorkflowProgress({ workflow, result }) {
  const researchFlow = result?.research_flow || {};
  const researchStatus = researchFlow.status || "standby";
  const researchReady = ["awaiting_user_confirmation", "evidence_ready"].includes(researchStatus);
  const currentStage = workflow.final
    ? PROVE_WORKFLOW_STEPS.length
    : Math.max(1, Math.min(
      PROVE_WORKFLOW_STEPS.length,
      researchReady || researchStatus === "unavailable" ? Math.max(3, workflow.stage || 1) : workflow.stage || 1,
    ));
  const processMessage = researchFlow.trigger === "auto_fallback"
    ? "PROVE 1차 판정에 필요한 사실이 부족해 AI 웹검증을 자동 실행했습니다. 웹 사실은 확인 전까지 결정트리에 들어가지 않습니다."
    : researchFlow.trigger === "web_result_handoff"
      ? "전체 탭에서 확보한 AI 웹검증 자료를 다시 불러왔습니다. 사용자가 확인한 사실만 PROVE가 재계산합니다."
      : researchFlow.trigger === "manual"
        ? "사용자가 요청한 AI 웹검증 결과입니다. 확인한 상품 사실만 PROVE 재계산에 사용합니다."
        : "PROVE가 먼저 판정하고, 사실이 부족한 경우에만 AI 웹검증을 자동 실행합니다.";
  return (
    <div className="aiw-workflow" aria-label="PROVE 판정 진행 단계">
      <div className="aiw-workflow-head">
        <strong>판정 진행 {currentStage}/{PROVE_WORKFLOW_STEPS.length}</strong>
        <span>{researchFlow.trigger === "auto_fallback" ? "AI 웹검증 자동 보완" : workflow.action_label}</span>
      </div>
      <ol>
        {PROVE_WORKFLOW_STEPS.map((label, index) => {
          const step = index + 1;
          const done = workflow.final || step < currentStage;
          const current = !workflow.final && step === currentStage;
          return (
            <li
              key={label}
              className={`${done ? "is-done" : ""} ${current ? "is-current" : ""}`.trim()}
              aria-current={current ? "step" : undefined}
            >
              <i>{done ? <Check size={11} /> : step}</i>
              <span>{label}</span>
            </li>
          );
        })}
      </ol>
      <p>{processMessage}<br />사용자는 세번이 아니라 상품 사실만 확인합니다. 세번은 PROVE 결정트리가 판정합니다.</p>
    </div>
  );
}

function decisionTreeLabel(result, tree) {
  const outputField = result?.classification_tree_output_field || result?.prove?.rule_set?.output_field || "";
  const labels = {
    chapter39_tree_shadow: "제39류 검수 결정트리",
    chapter42_tree_shadow: "제42류 검수 결정트리",
    apparel_tree_shadow: "제61·62류 검수 결정트리",
    footwear_tree_shadow: "제64류 검수 결정트리",
    chapter71_tree_shadow: "제71류 검수 결정트리",
    chapter72_tree_shadow: "제72류 검수 결정트리",
    chapter73_tree_shadow: "제73류 검수 결정트리",
    machinery_tree_shadow: "제84류 검수 결정트리",
    chapter85_tree_shadow: "제85류 검수 결정트리",
    chapter90_tree_shadow: "제90류 검수 결정트리",
  };
  return labels[outputField]
    || (tree?.supported_chapter ? `제${tree.supported_chapter}류 검수 결정트리` : "검수 결정트리");
}

function SearchEnrichment({ enrichment }) {
  if (!enrichment?.used || enrichment?.provider !== "naver") return null;
  const sources = Array.isArray(enrichment.sources)
    ? enrichment.sources.filter((source) => safeHttpUrl(source?.url)).slice(0, 5)
    : [];
  return (
    <div className="aiw-search-enrichment" data-testid="external-search-enrichment">
      <div className="aiw-search-enrichment-head">
        <em className="aiw-badge aiw-badge-search"><Search size={12} /> 외부 검색으로 보강됨</em>
        <span>제품명·재질·용도 표현을 넓힌 참고자료이며 세번을 직접 확정하지 않습니다.</span>
      </div>
      {sources.length > 0 && (
        <div className="aiw-search-enrichment-sources" aria-label="외부 검색 출처">
          {sources.map((source, index) => (
            <a
              key={`${source.url}-${index}`}
              href={safeHttpUrl(source.url)}
              target="_blank"
              rel="noreferrer"
            >
              <ExternalLink size={12} /> {source.title || source.url}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function EngineComparison({ result, query, onAnswerFact, busy, confirmedFacts }) {
  if (!result) return null;
  const legacy = result.legacy_classifier || {};
  const tree = result.classification_tree || result.apparel_tree || {};
  const comparison = result.comparison || {};
  const prove = result.prove || {};
  const proof = prove.proof || {};
  const supported = String(tree.action || "").startsWith("supported");
  const proved = isProveFinal(result);
  const workflow = proveWorkflow(result, tree, comparison, prove, proved);
  const tone = workflowTone(workflow);
  const dialogue = productDialogue(tree);
  const sourceDirectionWithdrawn = dialogue.product_intake?.kind === 'source_direction'
    && !dialogue.conditional_assessment && dialogue.status !== 'candidate_ready';
  const supportedCode = formatHsk(sourceDirectionWithdrawn ? null : tree.supported_hsk || tree.supported_hs6 || tree.supported_hs4);

  return (
    <section className="aiw-decision-card">
      <div className="aiw-section-heading">
        <span><ShieldCheck size={16} /> PROVE 심볼릭 판정</span>
        {comparison.narrative_conflict && <em className="aiw-badge aiw-badge-danger">AI 설명 후보 충돌</em>}
        <em className={`aiw-badge aiw-badge-${tone}`}>{workflow.label}</em>
      </div>

      <SearchEnrichment enrichment={legacy.search_enrichment} />

      <div className={`aiw-callout aiw-callout-${tone === "success" ? "success" : tone === "danger" ? "warning" : "info"}`}>
        {tone === "danger" ? <AlertTriangle size={16} /> : <ShieldCheck size={16} />}
        <span><strong>{workflow.label}</strong><br />{workflow.description}</span>
      </div>

      {result.narrative_guard_applied && (
        <div className="aiw-callout aiw-callout-warning">
          <AlertTriangle size={16} /> AI 설명과 심볼릭 판정 상태가 달라 세번 확정 표현을 차단했습니다.
        </div>
      )}

      <ProveWorkflowProgress workflow={workflow} result={result} />

      <div className="aiw-engine-grid">
        <div className={`aiw-engine-box ${supported || workflow.candidate_ready ? "is-supported" : ""}`}>
          <div className="aiw-engine-label">{decisionTreeLabel(result, tree)}</div>
          <strong>{supportedCode}</strong>
          <span>{workflow.description || treeActionLabel(tree.action)}</span>
        </div>
      </div>

      <div className="aiw-proof-summary">
        <span>LLM 역할 <b>사실 추출만</b></span>
        <span>프롬프트 <b>{proof.prompt_stage_count || result?.fact_interpretation?.prompt_stage_count || 1}단계</b></span>
        <span>사용자 확인 <b>{proof.confirmed_fact_count || 0}건</b></span>
        <span>문서 명시 <b>{proof.document_fact_count || 0}건</b></span>
        <span>규칙 개정 <b>{prove?.rule_set?.revision || tree.revision || "운영 검수본"}</b></span>
      </div>

      {tree.supported_chapter && !sourceDirectionWithdrawn && (
        <div className="aiw-path">
          <span>제{tree.supported_chapter}류</span>
          {tree.supported_hs4 && <><b>→</b><span>{tree.supported_hs4}호</span></>}
          {tree.supported_hs6 && <><b>→</b><span>{formatHsk(tree.supported_hs6)}</span></>}
          {tree.supported_hsk && <><b>→</b><span>{formatHsk(tree.supported_hsk)}</span></>}
        </div>
      )}

      <ProductFactDialoguePanel key={query} tree={tree} onAnswer={onAnswerFact} disabled={busy} answers={confirmedFacts} />

      <div className="aiw-shadow-note">
        PROVE는 LLM이 추출한 사실을 검수 결정트리와 결정론적 파서로 판정하며, 확인되지 않은 후보 번호는 화면 결론으로 사용하지 않습니다.
      </div>
      <ProveDecisionGraph result={result} query={query} />
      <ProveEvaluationSummary metrics={result?.evaluation_metrics} />
    </section>
  );
}

function DocumentPanel({ documents, busy, onUpload, onAnalyze, fileRef }) {
  const reanalysisCount = (documents || []).filter((document) => document.requires_reanalysis).length;
  return (
    <section className="aiw-side-section">
      <div className="aiw-section-heading"><span><Paperclip size={15} /> 분류 서류</span></div>
      {reanalysisCount > 0 && (
        <div className="aiw-callout aiw-callout-warning">
          <AlertTriangle size={15} /> 구형 분석 {reanalysisCount}건은 부정 표현 안전검사가 없어 다시 분석해야 합니다.
        </div>
      )}
      <label className="aiw-upload">
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx,.xls,.xlsx,.csv,.txt"
          disabled={busy}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) onUpload(file);
            event.target.value = "";
          }}
        />
        <Paperclip size={15} /> 서류 업로드
        <small>PDF·이미지·Word·Excel·CSV·TXT, 최대 20MB</small>
      </label>
      <div className="aiw-document-list">
        {(documents || []).map((document) => {
          const requiresReanalysis = Boolean(document.requires_reanalysis);
          return (
            <div className="aiw-document" key={document.evidence_id}>
              <FileText size={16} />
              <div>
                <strong title={document.file_name}>{document.file_name}</strong>
                <span>{requiresReanalysis
                  ? "재분석 필요 · 구형 사실은 결정트리에서 제외됨"
                  : document.analyzed
                    ? `분석 완료 · ${document.analysis_method || "사실 추출"}`
                    : "분석 대기"}</span>
              </div>
              {(!document.analyzed || requiresReanalysis) && (
              <button disabled={busy} onClick={() => onAnalyze(document.evidence_id)}>
                <FileSearch size={14} /> {requiresReanalysis ? "다시 분석" : "분석"}
              </button>
              )}
            </div>
          );
        })}
        {(!documents || documents.length === 0) && <p className="aiw-empty">인보이스·사양서·카탈로그를 추가해 주세요.</p>}
      </div>
    </section>
  );
}

function interpretedFactRows(value) {
  if (Array.isArray(value)) return value.filter((fact) => fact?.key);
  if (value && typeof value === "object") {
    return Object.entries(value).map(([key, factValue]) => ({
      key,
      value: factValue,
      status: "proposed",
      source_type: "llm",
    }));
  }
  return [];
}

function pendingFactRows(result, confirmedFacts) {
  const interpreted = interpretedFactRows(result?.interpreted_facts);
  const researched = interpretedFactRows(result?.research?.prove_fact_candidates)
    .map((fact) => ({ ...fact, research: true, status: "proposed" }));
  const dialogue = productDialogue(result?.classification_tree || result?.apparel_tree || {});
  if ([...dialogue.questions, ...(dialogue.deferred_questions || [])].some(q => q.fact_key === 'product_description')) {
    const description = researchDescriptionProposal(result?.research?.summary);
    if (description) researched.push({ ...description, key: description.fact_key, research: true });
  }
  const previous = Object.entries(result?.unverified_previous_facts || {})
    .map(([key, value]) => ({ key, value, status: "proposed", previous: true }));
  const combined = [...interpreted, ...researched, ...previous]
    .filter(fact => isProductQuestion(fact))
    .filter(fact => !String(fact.key || '').startsWith('cabinet_'))
    .filter((fact) => !Object.prototype.hasOwnProperty.call(confirmedFacts || {}, fact.key));
  const valuesByKey = new Map();
  combined.forEach((fact) => {
    const values = valuesByKey.get(fact.key) || new Set();
    values.add(JSON.stringify(fact.value));
    valuesByKey.set(fact.key, values);
  });
  const seen = new Set();
  return combined.filter((fact) => {
    const signature = `${fact.key}:${JSON.stringify(fact.value)}`;
    if (seen.has(signature)) return false;
    seen.add(signature);
    return true;
  }).map((fact) => ({
    ...fact,
    conflict: (valuesByKey.get(fact.key)?.size || 0) > 1,
  }));
}

function FactPanel({ result, confirmedFacts, busy, onConfirmFact, onConfirmFacts }) {
  const ledger = Array.isArray(result?.fact_ledger) ? result.fact_ledger : [];
  const researchFacts = Array.isArray(result?.research?.facts)
    ? result.research.facts.map((fact) => ({ ...fact, status: fact.status || "internet_source", research: true }))
    : [];
  const rows = [...ledger, ...researchFacts].filter(isProductQuestion).filter(f => !String(f.key || '').startsWith('cabinet_'));
  const pendingInterpreted = pendingFactRows(result, confirmedFacts);
  const pendingFactMap = Object.fromEntries(
    pendingInterpreted.filter((fact) => !fact.conflict).map((fact) => [fact.key, fact.value]),
  );
  const confirmableCount = Object.keys(pendingFactMap).length;
  return (
    <section className="aiw-side-section">
      <div className="aiw-section-heading"><span><ShieldCheck size={15} /> 상품 사실 확인</span></div>
      <div className="aiw-confirmed-pills">
        {Object.entries(confirmedFacts || {}).map(([key, value]) => (
          <span key={key}><Check size={11} /> {factLabel(key)}: {valueLabel(value, key)}</span>
        ))}
      </div>
      {pendingInterpreted.length > 0 && (
        <div className="aiw-interpreted-review">
          <div className="aiw-interpreted-review-head">
            <div>
              <strong>확인할 상품 사실</strong>
              <span>현재 상품과 맞는 사실만 확인해 주세요. 이전 답변과 AI 제안은 확인 전에는 판정에 사용하지 않습니다.</span>
            </div>
            {confirmableCount > 0 && (
              <button disabled={busy} onClick={() => onConfirmFacts(pendingFactMap)}>
                <CheckCircle2 size={13} /> 상품 사실 {confirmableCount}건이 맞습니다
              </button>
            )}
          </div>
          <div className="aiw-fact-list">
            {pendingInterpreted.map((fact, index) => (
              <div className={`aiw-fact is-interpreted ${fact.conflict ? "is-conflict" : ""}`} key={`${fact.key}-${fact.value}-${index}`}>
                <div className="aiw-fact-main">
                  <span>{fact.label || factLabel(fact.key)}</span>
                  <strong>{valueLabel(fact.value)}</strong>
                </div>
                <div className="aiw-fact-meta">
                  {fact.previous ? "이전 저장 답변 · 출처 재확인 필요" : fact.research ? "AI 웹검증 사실 후보" : fact.status === "proposed" ? "LLM 의미 제안" : "자연어 명시 사실"} · 사용자 확인 전
                </div>
                {fact.source_span && <q>{fact.source_span}</q>}
                <button disabled={busy} onClick={() => onConfirmFact(fact.key, fact.value)}>
                  이 사실이 맞습니다
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="aiw-fact-list">
        {rows.map((fact, index) => {
          const accepted = String(confirmedFacts?.[fact.key] ?? "") === String(fact.value ?? "");
          const requiresReanalysis = fact.status === "requires_reanalysis";
          return (
            <div className={`aiw-fact ${fact.conflict ? "is-conflict" : ""}`} key={`${fact.key}-${fact.value}-${index}`}>
              <div className="aiw-fact-main">
                <span>{factLabel(fact.key)}</span>
                <strong>{valueLabel(fact.value)}</strong>
              </div>
              <div className="aiw-fact-meta">
                {fact.conflict ? "문서 간 충돌" : fact.research ? "인터넷 조사" : requiresReanalysis
                  ? "구형 분석 · 재분석 필요"
                  : fact.status === "document_explicit" ? "문서 명시" : "AI 추론"}
                {fact.source_file_name ? ` · ${fact.source_file_name}` : ""}
              </div>
              {fact.source_span && <q>{fact.source_span}</q>}
              <button disabled={busy || accepted || requiresReanalysis} onClick={() => onConfirmFact(fact.key, fact.value)}>
                {accepted ? <><CheckCircle2 size={13} /> 사용자 확인</>
                  : requiresReanalysis ? "재분석 후 채택" : "사실로 채택"}
              </button>
            </div>
          );
        })}
        {rows.length === 0 && pendingInterpreted.length === 0
          && <p className="aiw-empty">문서를 분석하거나 질문에 답하면 사실이 여기에 모입니다.</p>}
      </div>
    </section>
  );
}

function ResearchPanel({ result, busy, onResearch }) {
  const sources = Array.isArray(result?.research?.sources) ? result.research.sources : [];
  const flow = result?.research_flow || {};
  const statusLabel = flow.status === "awaiting_user_confirmation"
    ? `사실 후보 ${flow.fact_candidate_count || 0}건 확인 필요`
    : flow.status === "evidence_ready" ? "출처 확인 완료"
      : flow.status === "unavailable" ? "확인 가능한 웹자료 부족"
        : "필요 시 자동 실행";
  return (
    <section className="aiw-side-section">
      <div className="aiw-section-heading">
        <span><Search size={15} /> AI 웹검증</span>
        <button className="aiw-icon-action" disabled={busy} onClick={onResearch}><RefreshCw size={13} /> AI 웹검증</button>
      </div>
      <p className="aiw-helper"><strong>{statusLabel}</strong><br />확정 검토에서 부족한 상품 사실만 제조사·공식자료에서 확인하며, 사용자 확인 전에는 결정 입력으로 사용하지 않습니다.</p>
      {result?.research?.summary && (
        <MarkdownContent content={compactResearchSummary(result.research.summary)} className="aiw-research-summary" />
      )}
      <div className="aiw-source-list">
        {sources.map((source, index) => {
          const href = safeHttpUrl(source.url);
          if (!href) return null;
          return (
            <a href={href} target="_blank" rel="noreferrer" key={`${href}-${index}`}>
              <ExternalLink size={12} /> {source.title || source.source_type || "조사 출처"}
            </a>
          );
        })}
      </div>
    </section>
  );
}

export default function AiClassificationWorkspace({ q, productDetails, onNav, onReportData, autoResearch = false }) {
  const authenticated = canUseWorkspaceApi();
  const workspaceId = useMemo(() => workspaceIdForQuery(q), [q]);
  const proveQuery = useMemo(() => proveQueryText(q, productDetails), [q, productDetails]);
  const [result, setResult] = useState(null);
  const [messages, setMessages] = useState([]);
  const [confirmedFacts, setConfirmedFacts] = useState({});
  const [identityConfirmed, setIdentityConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [statusText, setStatusText] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const generationRef = useRef(0);
  const factSourceRef = useRef({});
  const [factRevision, setFactRevision] = useState(0);

  useEffect(() => {
    const changed = ({ detail }) => {
      if (detail?.key !== treeFactKey(q) || detail.source === factSourceRef.current) return;
      generationRef.current += 1;
      setResult(null);
      onReportData?.(null);
      setConfirmedFacts(detail.facts || {});
      setFactRevision(value => value + 1);
    };
    window.addEventListener(TREE_FACTS_UPDATED_EVENT, changed);
    return () => window.removeEventListener(TREE_FACTS_UPDATED_EVENT, changed);
  }, [q, onReportData]);

  const runAssistant = useCallback(async (message, options = {}) => {
    const text = String(message || "").trim();
    if (!text || busy) return null;
    const generation = ++generationRef.current;
    const baseMessages = options.history || messages;
    if (!options.silentUser) setMessages([...baseMessages, { role: "user", content: text }]);
    setBusy(true);
    setResult(null);
    onReportData?.(null);
    setError("");
    setStatusText(options.status || "PROVE가 먼저 판정하고 필요한 경우 AI 웹검증을 자동 실행합니다.");
    try {
      const response = await runWorkspaceAssistant(workspaceId, {
        message: text,
        history: baseMessages,
        conversation_id: result?.conversation_id,
        confirmed_facts: productFactsOnly(options.confirmedFacts || confirmedFacts),
        confirmed_facts_schema: CONFIRMED_FACTS_SCHEMA,
        product_fact_dialogue: true,
        identity_confirmed: options.identityConfirmed ?? identityConfirmed,
        research: Boolean(options.research),
        auto_research_if_needed: options.autoResearchIfNeeded !== false,
        research_trigger: options.research ? options.researchTrigger || "manual" : undefined,
      });
      if (generation !== generationRef.current) return null;
      const nextMessages = [...baseMessages, { role: "user", content: text },
        { role: "assistant", content: response.answer || "분류 경로를 갱신했습니다." }].slice(-40);
      setMessages(nextMessages);
      setResult(response);
      setConfirmedFacts(productFactsOnly(response.confirmed_facts || options.confirmedFacts || confirmedFacts));
      setIdentityConfirmed(Boolean(response.identity_confirmed));
      const report = classificationReport(q, response);
      if (onReportData && report) onReportData(report);
      return response;
    } catch (requestError) {
      if (generation === generationRef.current) setError(requestError.message || "분류 작업을 완료하지 못했습니다.");
      return null;
    } finally {
      if (generation === generationRef.current) {
        setBusy(false);
        setStatusText("");
      }
    }
  }, [busy, confirmedFacts, identityConfirmed, messages, onReportData, q, result?.conversation_id, workspaceId]);

  useEffect(() => {
    if (!authenticated || !q) return undefined;
    const generation = ++generationRef.current;
    let cancelled = false;
    setBusy(true);
    setStatusText("PROVE 1차 판정 후 필요한 경우 AI 웹검증을 자동 실행합니다.");
    setError("");
    (async () => {
      try {
        const item = await ensureWorkspace(proveQuery, "ai", { workspaceId, title: q });
        if (cancelled || generation !== generationRef.current) return;
        const saved = savedAssistant(item);
        const savedMessages = normalizeMessages(saved.messages);
        // 전체 탭 사전 점검 카드에서 답한 사실은 PROVE 첫 계산부터 확인 사실로 들어간다.
        // 현재 로컬 스냅샷(빈 값 포함)을 우선하고 구형 저장값은 재확인 후보로 격리한다.
        const facts = hasTreeFactSnapshot(q) ? loadTreeFacts(q)
          : saved.confirmed_facts_schema === CONFIRMED_FACTS_SCHEMA ? saved.confirmed_facts || {} : {};
        const identity = Boolean(saved.identity_confirmed);
        setMessages(savedMessages);
        setConfirmedFacts(facts);
        if (!hasTreeFactSnapshot(q)) saveTreeFacts(q, facts, factSourceRef.current);
        setIdentityConfirmed(identity);
        const response = await runWorkspaceAssistant(workspaceId, {
          message: q,
          history: savedMessages,
          conversation_id: saved.conversation_id,
          confirmed_facts: productFactsOnly(facts),
          confirmed_facts_schema: CONFIRMED_FACTS_SCHEMA,
        product_fact_dialogue: true,
          unverified_previous_facts: loadUnverifiedTreeFacts(q),
          identity_confirmed: identity,
          research: Boolean(autoResearch),
          auto_research_if_needed: true,
          research_trigger: autoResearch ? "web_result_handoff" : undefined,
        });
        if (cancelled || generation !== generationRef.current) return;
        setResult(response);
        setMessages([...savedMessages, { role: "user", content: q },
          { role: "assistant", content: response.answer || "분류 경로를 확인했습니다." }].slice(-40));
        setConfirmedFacts(productFactsOnly(response.confirmed_facts || facts));
        setIdentityConfirmed(Boolean(response.identity_confirmed));
        const report = classificationReport(q, response);
        if (onReportData && report) onReportData(report);
      } catch (requestError) {
        if (!cancelled) setError(requestError.message || "분류 작업공간을 열지 못했습니다.");
      } finally {
        if (!cancelled && generation === generationRef.current) {
          setBusy(false);
          setStatusText("");
        }
      }
    })();
    return () => { cancelled = true; generationRef.current += 1; };
  }, [authenticated, autoResearch, onReportData, proveQuery, q, workspaceId, factRevision]);

  const upload = async (file) => {
    if (!file || busy) return;
    setBusy(true);
    setError("");
    setStatusText(`${file.name}을(를) 업로드하고 분류 사실을 추출하고 있습니다.`);
    try {
      const attachment = await uploadWorkspaceAttachment(workspaceId, file);
      await analyzeWorkspaceAttachment(workspaceId, attachment.evidenceId);
      setBusy(false);
      await runAssistant(`업로드한 ${file.name}의 내용을 반영해 분류해 주세요.`, {
        history: messages,
        confirmedFacts,
        identityConfirmed: false,
        status: "문서 사실을 결정트리에 적용하고 있습니다.",
      });
    } catch (uploadError) {
      setError(uploadError.message || "첨부 문서를 처리하지 못했습니다.");
      setBusy(false);
      setStatusText("");
    }
  };

  const analyze = async (evidenceId) => {
    if (busy) return;
    setBusy(true);
    setError("");
    setStatusText("문서에서 분류 사실을 추출하고 있습니다.");
    try {
      await analyzeWorkspaceAttachment(workspaceId, evidenceId);
      setBusy(false);
      await runAssistant("분석한 문서의 사실을 결정트리에 반영해 주세요.", { history: messages });
    } catch (analysisError) {
      setError(analysisError.message || "문서를 분석하지 못했습니다.");
      setBusy(false);
      setStatusText("");
    }
  };

  const confirmFact = async (key, value) => {
    if (busy || isLegalDecisionKey(key)) return;
    const next = saveTreeFacts(q, { ...confirmedFacts, [key]: value }, factSourceRef.current);
    setConfirmedFacts(next);
    await runAssistant(value == null || value === 'edit_required' ? `${factLabel(key)} 정보를 수정합니다. 새 값을 확인할 때까지 미확인으로 둡니다.` : value === 'unknown' ? `${factLabel(key)} 정보를 아직 확인하지 못했습니다.` : `${factLabel(key)}을(를) '${valueLabel(value, key)}'(으)로 확인합니다.`, {
      history: messages,
      confirmedFacts: next,
      status: "확인한 사실로 결정트리를 다시 계산하고 있습니다.",
    });
  };

  const confirmFacts = async (facts) => {
    const entries = Object.entries(facts || {});
    if (entries.length === 0) return;
    if (busy) return;
    const next = saveTreeFacts(q, { ...confirmedFacts, ...facts }, factSourceRef.current);
    setConfirmedFacts(next);
    await runAssistant(`자연어에서 추출한 상품 사실 ${entries.length}건이 맞음을 확인합니다.`, {
      history: messages,
      confirmedFacts: next,
      status: "확인한 사실로 최종 PROVE 판정을 계산하고 있습니다.",
    });
  };

  const confirmIdentity = async () => {
    setIdentityConfirmed(true);
    await runAssistant("첨부한 서류가 현재 분류할 제품의 자료임을 확인합니다.", {
      history: messages,
      identityConfirmed: true,
      status: "상품 동일성 확인을 반영하고 있습니다.",
    });
  };

  const submit = () => {
    const value = inputRef.current?.value || "";
    if (inputRef.current) inputRef.current.value = "";
    runAssistant(value, { history: messages });
  };

  if (!authenticated) {
    return (
      <div>
        <div className="aiw-callout aiw-callout-warning">PROVE 근거 판정과 서류 저장을 사용하려면 로그인해 주세요.</div>
        <AiModePanel q={q} onNav={onNav} onReportData={onReportData} />
      </div>
    );
  }

  const documents = result?.documents || [];

  return (
    <div className="aiw-shell audit-fade-in">
      <header className="aiw-header">
        <div>
          <span className="aiw-kicker"><ShieldCheck size={14} /> PROVE 세번분류 엔진</span>
          <h2>{q}</h2>
          <p>LLM은 자연어를 분류 사실로 해체하고, 검수 결정트리가 근거가 충분할 때만 세번을 판정합니다.</p>
        </div>
        <div className="aiw-header-actions">
          {identityConfirmed ? <span className="aiw-badge aiw-badge-success"><CheckCircle2 size={13} /> 상품 확인됨</span>
            : documents.length > 0 && <button onClick={confirmIdentity} disabled={busy}><ShieldCheck size={14} /> 이 제품의 서류가 맞습니다</button>}
        </div>
      </header>

      <div className="aiw-grid">
        <main className="aiw-conversation">
          <EngineComparison result={result} query={q} onAnswerFact={confirmFact} busy={busy} confirmedFacts={confirmedFacts} />
          <div className="aiw-message-list">
            {messages.length > 0 && <p style={{fontSize:12,color:'var(--audit-muted, #667085)'}}>대화 기록 · 과거 답변이 포함되어 있습니다. 현재 판정은 위의 PROVE 상태를 확인해 주세요.</p>}
            {messages.map((message, index) => (
              <div className={`aiw-message ${message.role}`} key={`${message.role}-${index}`}>
                {message.role === "assistant" && <Bot size={16} />}
                <div>{message.role === "assistant" ? <AssistantAnswer content={message.content} /> : message.content}</div>
              </div>
            ))}
          </div>
          {busy && <div className="aiw-progress"><Loader2 size={15} className="aiw-spin" /> {statusText || "처리 중입니다."}</div>}
          {error && <div className="aiw-error"><AlertTriangle size={15} /> {error}</div>}
          <div className="aiw-composer">
            <input
              ref={inputRef}
              placeholder="이 분류에 대해 질문하거나 추가 사실을 알려주세요"
              disabled={busy}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.nativeEvent.isComposing) submit();
              }}
            />
            <button disabled={busy} onClick={submit}><Send size={16} /> 보내기</button>
          </div>
        </main>

        <aside className="aiw-sidebar">
          <DocumentPanel documents={documents} busy={busy} onUpload={upload} onAnalyze={analyze} fileRef={fileRef} />
          <FactPanel
            result={result}
            confirmedFacts={confirmedFacts}
            busy={busy}
            onConfirmFact={confirmFact}
            onConfirmFacts={confirmFacts}
          />
          <ResearchPanel
            result={result}
            busy={busy}
            onResearch={() => runAssistant("문서에서 부족한 제품 사실을 인터넷 자료로 보완해 주세요.", {
              history: messages,
              research: true,
              autoResearchIfNeeded: false,
              researchTrigger: "manual",
              status: "AI 웹검증으로 제조사·공식자료를 확인하고 있습니다.",
            })}
          />
        </aside>
      </div>
    </div>
  );
}
